import React, { memo } from 'react';
import {
  Box,
  Grid,
} from '@mui/material';
import './index.css';

const DesktopGameTile = memo(function DesktopGameTile({
  game,
  platformId,
  isSelected,
  controllerHighlightActive,
  imageUrl,
  hasBoxArt,
  onSelect,
  onBoxArtError,
}) {
  return (
    <Grid item xs={6} sm={4} md={3} lg={2} sx={{ overflow: 'visible' }}>
      <Box
        data-game-id={game.id}
        className={[
          'grid-game-tile',
          isSelected ? 'grid-game-tile-selected' : '',
          isSelected && controllerHighlightActive ? 'grid-game-tile-controller-selected' : '',
        ].filter(Boolean).join(' ')}
        sx={{
          position: 'relative',
          width: 239,
          height: platformId === '100' ? 239 : 340,
          cursor: 'pointer',
          borderRadius: 3,
          boxShadow: isSelected ? 8 : 2,
          border: isSelected ? '3px solid #4a90e2' : '1px solid rgba(255,255,255,0.2)',
          overflow: 'hidden',
          contentVisibility: 'auto',
          containIntrinsicSize: platformId === '100' ? '239px 239px' : '340px 239px',
          zIndex: isSelected ? 20 : 1,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: isSelected && controllerHighlightActive ? undefined : 'scale(1.05)',
          },
        }}
        onClick={onSelect}
      >
        <Box position="relative" width="100%" height="100%">
          <Box
            component="img"
            src={imageUrl}
            alt={game.title}
            title={game.title}
            loading="lazy"
            decoding="async"
            onError={onBoxArtError}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          {platformId === '99' && game.grade && (
            <Box
              sx={{
                position: 'absolute',
                top: 5,
                left: 5,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: '#FFD700',
                px: 1,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontWeight: 'bold',
                zIndex: 3
              }}
            >
              {game.grade}
            </Box>
          )}

          {Boolean(game.favorite) && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                padding: '2px 6px',
                color: 'gold',
                fontSize: '1.2rem',
                lineHeight: 1,
                zIndex: 2,
              }}
            >
              ★
            </Box>
          )}

          {game.region === 'Japan' && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                bottom: 8,
                left: 8,
                backgroundColor: 'rgba(255,255,255,0.1)',
                borderRadius: '50%',
                padding: '2px 6px',
                fontSize: '1.2rem',
                zIndex: 2,
              }}
            >
              🇯🇵
            </Box>
          )}
        </Box>

        {!hasBoxArt && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              px: 1,
              py: 0.5,
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '0.9rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {game.title}
          </Box>
        )}
      </Box>
    </Grid>
  );
}, (prev, next) => (
  prev.game?.id === next.game?.id &&
  prev.game?.title === next.game?.title &&
  prev.game?.grade === next.game?.grade &&
  prev.game?.favorite === next.game?.favorite &&
  prev.game?.region === next.game?.region &&
  prev.platformId === next.platformId &&
  prev.isSelected === next.isSelected &&
  prev.controllerHighlightActive === next.controllerHighlightActive &&
  prev.imageUrl === next.imageUrl &&
  prev.hasBoxArt === next.hasBoxArt &&
  prev.onBoxArtError === next.onBoxArtError
));

export default function GridView({
  games = [],
  selectedGame,
  setSelectedGame,
  isMobile,
  setMobileModalOpen,
  setBoxArtUrl,
  getCachedBoxArtUrl,
  normalizeBoxArtUrl,
  fallbackBoxArtUrl,
  controllerHighlightActive = false,
  onGridGameSelect,
}) {
  const platformId = String(games?.[0]?.platform_id ?? '');

    const formatCondition = (condition) => {
    switch(condition) {
      case 'COMPLETE': return '📦 Complete';
      case 'LOOSE': return '💿 Loose';
      case 'SEALED': return '🔒 Sealed';
      case 'JUNK': return '🗑️ Junk';
      case 'MANUAL': return '📃 Missing Manual';
      default: return condition || 'N/A';
    }
  };

  // Helper function to get logo path
  function getConsoleLogoPath(platformName) {
    try {
      return require(`./assets/old-console-logos/${platformName.replace(/ /g, '_')}.png`);
    } catch (e) { 
      return null;
    }
  }

  const MOBILE_CARD_W = platformId === '100' ? 110 : 100;
  const MOBILE_CARD_H = platformId === '100' ? 110 : 150;

  const handleBoxArtError = (event) => {
    if (!fallbackBoxArtUrl) return;
    if (event?.target && event.target.src !== fallbackBoxArtUrl) {
      event.target.src = fallbackBoxArtUrl;
      event.target.onerror = null;
    }
  };

  return (
    <Box>
      <Box
        sx={{
          overflowY: 'auto',
          overflowX: 'visible',
          maxHeight: 'calc(100vh - 100px)',
          pt: 2,
          pb: 2,
          px: 1,
        }}
      >
        {isMobile ? (
          /* Uniform shelf grid on mobile */
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              justifyContent: 'flex-start',
              overflow: 'visible',
            }}
          >
            {games.map((game) => {
              const isSelected = selectedGame?.id === game.id;
              const cachedUrl = getCachedBoxArtUrl?.(game);
              const normalizedUrl = normalizeBoxArtUrl?.(game?.box_art_url);
              const imageUrl = cachedUrl || normalizedUrl || fallbackBoxArtUrl;
              const hasBoxArt = Boolean(cachedUrl || normalizedUrl);

              return (
                <Box
                  key={game.id}
                  data-game-id={game.id}
                  className={[
                    'grid-game-tile',
                    isSelected ? 'grid-game-tile-selected' : '',
                    isSelected && controllerHighlightActive ? 'grid-game-tile-controller-selected' : '',
                  ].filter(Boolean).join(' ')}
                  sx={{
                    position: 'relative',
                    width: MOBILE_CARD_W,
                    height: MOBILE_CARD_H,
                    flex: `0 0 ${MOBILE_CARD_W}px`,
                    cursor: 'pointer',
                    borderRadius: 2,
                    boxShadow: isSelected ? 8 : 2,
                    border: isSelected ? '2px solid #4a90e2' : '1px solid rgba(255,255,255,0.2)',
                    overflow: 'hidden',
                    contentVisibility: 'auto',
                    containIntrinsicSize: `${MOBILE_CARD_W}px ${MOBILE_CARD_H}px`,
                    zIndex: isSelected ? 20 : 1,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                    '&:active': { transform: 'scale(0.96)' },
                  }}
                  onClick={() => {
                    if (onGridGameSelect) {
                      onGridGameSelect(game, imageUrl);
                      return;
                    }
                    setBoxArtUrl(imageUrl);
                    setSelectedGame(game);
                    setMobileModalOpen(true);
                  }}
                >
                  <Box position="relative" width="100%" height="100%">
                    <Box
                      component="img"
                      src={imageUrl}
                      alt={game.title}
                      title={game.title}
                      loading={games.length > 150 ? 'lazy' : 'eager'}
                      decoding="async"
                      onError={handleBoxArtError}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    {platformId === '99' && game.grade && (
                      <Box sx={{ position: 'absolute', top: 4, left: 4, backgroundColor: 'rgba(0,0,0,0.7)', color: '#FFD700', px: 0.75, borderRadius: 1, fontSize: '0.65rem', fontWeight: 'bold', zIndex: 3 }}>
                        {game.grade}
                      </Box>
                    )}
                    {Boolean(game.favorite) && (
                      <Box component="span" sx={{ position: 'absolute', top: 5, right: 5, color: 'gold', fontSize: '0.95rem', lineHeight: 1, zIndex: 2, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>★</Box>
                    )}
                    {game.region === 'Japan' && (
                      <Box component="span" sx={{ position: 'absolute', bottom: 20, left: 4, fontSize: '0.85rem', zIndex: 2 }}>🇯🇵</Box>
                    )}
                    {/* Always-visible title fade */}
                    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0) 100%)', color: 'white', px: 0.5, pt: 1.5, pb: 0.4, fontSize: '0.6rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', textAlign: 'center', zIndex: 2 }}>
                      {game.title}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : (
        <Grid container spacing={2} sx={{ overflow: 'visible' }}>
          {games.map((game) => {
              const isSelected = selectedGame?.id === game.id;
              const cachedUrl = getCachedBoxArtUrl?.(game);
              const normalizedUrl = normalizeBoxArtUrl?.(game?.box_art_url);
              const imageUrl = cachedUrl || normalizedUrl || fallbackBoxArtUrl;
              const hasBoxArt = Boolean(cachedUrl || normalizedUrl);

              return (
              <DesktopGameTile
                key={game.id}
                game={game}
                platformId={platformId}
                isSelected={isSelected}
                controllerHighlightActive={controllerHighlightActive}
                imageUrl={imageUrl}
                hasBoxArt={hasBoxArt}
                onBoxArtError={handleBoxArtError}
                onSelect={() => {
                  if (onGridGameSelect) {
                    onGridGameSelect(game, imageUrl);
                    return;
                  }
                  setBoxArtUrl(imageUrl);
                  setSelectedGame(game);
                }}
              />
            );
          })}
        </Grid>
        )}
      </Box>
    </Box>
  );
}